import LoginPasswordRule from 'components/LoginPasswordRule';
import PasswordWithEye from 'components/PasswordWithEye';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Validator from 'utils/validator';
import { UserUpdateFormData } from '.';

interface PasswordGroupType {
  passwordEditable: boolean;
  usernameEditable: boolean;
  setPasswordEditable: Dispatch<SetStateAction<boolean>>;
}

const CurrentPasswordGroup = ({ passwordEditable, usernameEditable, setPasswordEditable }: PasswordGroupType) => {
  const { t } = useTranslation();
  const {
    register,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<UserUpdateFormData>();

  const onClickChangePassword = () => {
    setPasswordEditable(true);
  };

  useEffect(() => {
    Object.keys(errors).length > 0 && trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  return (
    <>
      {/* Current Password */}
      <div className={`label px-6 border-slate-300 ${passwordEditable ? '' : 'border-b'}`}>
        <label className="flex items-center label-text w-64">
          <span>{t('profile.formCurrentPassword')}</span>
        </label>
        <div className="flex flex-1 flex-col">
          {/* NOTE: 當僅修改 user name，需要輸入 currentPassword，給 API 做驗證 */}
          {passwordEditable || usernameEditable ? (
            <div>
              <PasswordWithEye
                register={{
                  ...register('currentPassword', {
                    required: Validator().required,
                    minLength: Validator().minLength(8, 63),
                    maxLength: Validator().maxLength(8, 63),
                    pattern: Validator().userPassword,
                  }),
                }}
                placeholder={t('profile.placeholderPassword')}
                errorsName={errors.currentPassword}
              />
              {errors.currentPassword && <span className="span-error">{errors.currentPassword.message}</span>}
            </div>
          ) : (
            <p>******</p>
          )}
        </div>
        {!(passwordEditable || usernameEditable) && (
          <button type="button" className="btn btn-link text-[#489EE5]" onClick={onClickChangePassword}>
            {t('modal.btnChangePassword')}
          </button>
        )}
      </div>
      {/* New Password */}
      {passwordEditable && (
        <div className="label px-6 border-slate-300">
          <label className="flex items-center label-text w-64">
            {t('profile.formNewPassword')}
            <LoginPasswordRule iconColor="text-gray-400" />
          </label>
          <div className="flex flex-1 flex-col">
            <div>
              <PasswordWithEye
                register={{
                  ...register('newPassword', {
                    required: Validator().required,
                    minLength: Validator().minLength(8, 63),
                    maxLength: Validator().maxLength(8, 63),
                    pattern: Validator().userPassword,
                    validate: {
                      currentPasswordDuplicate: (v) => v !== watch('currentPassword') || t('profile.newPasswordCurrentPasswordErrorMsg'),
                      matchWithConfirmPassword: (v) => v === watch('confirmPassword') || t('profile.newPasswordConfirmPasswordErrorMsg'),
                    },
                  }),
                }}
                placeholder={t('profile.placeholderNewPassword')}
                errorsName={errors.newPassword}
              />
              {errors.newPassword && <span className="span-error">{errors.newPassword.message}</span>}
            </div>
          </div>
        </div>
      )}
      {/* Confirm Password */}
      {passwordEditable && (
        <div className={`label px-6 ${passwordEditable ? 'border-b' : ''}`}>
          <label className="flex items-center label-text w-64"></label>
          <div className="flex flex-col space-y-1 flex-1">
            <div>
              <PasswordWithEye
                register={{
                  ...register('confirmPassword', {
                    required: Validator().required,
                    minLength: Validator().minLength(8, 63),
                    maxLength: Validator().maxLength(8, 63),
                    pattern: Validator().userPassword,
                    validate: {
                      matchWithNewPassword: (v) => v === watch('newPassword') || t('profile.confirmPasswordErrorMsg'),
                    },
                  }),
                }}
                placeholder={t('profile.placeholderConfirmPassword')}
                errorsName={errors.confirmPassword}
              />
              {errors.confirmPassword && <span className="span-error">{errors.confirmPassword.message}</span>}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CurrentPasswordGroup;
